export type AllergenLanguage = 'it' | 'en'

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

const ALLERGEN_MAPS: Record<
  AllergenLanguage,
  Array<{ name: string; keywords: string[] }>
> = {
  it: [
    {
      name: 'Glutine',
      keywords: [
        'pasta',
        'spaghetti',
        'lasagna',
        'lasagne',
        'gnocchi',
        'pizza',
        'farro',
        'orzo',
        'cous cous',
        'cous-cous',
        'pane',
        'crostini',
        'bruschetta',
        'tortellini',
        'ravioli',
        'tagliatelle',
        'fettuccine',
        'penne',
        'linguine',
        'bucatini',
        'pappardelle',
        'maltagliati',
        'passatelli',
        'canederli',
        'panzerotti',
        'calzone',
        'piadina',
        'maccaroni',
        'maccheroni',
      ],
    },
    {
      name: 'Latticini',
      keywords: [
        'burrata',
        'mozzarella',
        'parmigiano',
        'grana',
        'pecorino',
        'ricotta',
        'mascarpone',
        'gorgonzola',
        'taleggio',
        'formaggio',
        'stracciatella',
        'besciamella',
        'panna',
        'yogurt',
        'burro',
        'caciocavallo',
        'scamorza',
        'fontina',
        'asiago',
        'provola',
        'fiordilatte',
        'fior di latte',
        'grana padano',
        'latte',
      ],
    },
    {
      name: 'Uova',
      keywords: [
        'uova',
        'uovo',
        'maionese',
        'zabaione',
        'zabaglione',
        'tiramisu',
        'tiramisù',
        'frittata',
        'carbonara',
      ],
    },
    {
      name: 'Pesce',
      keywords: [
        'pesce',
        'salmone',
        'tonno',
        'branzino',
        'orata',
        'sgombro',
        'acciuga',
        'acciughe',
        'sardina',
        'sardine',
        'merluzzo',
        'nasello',
        'trota',
        'spigola',
        'sogliola',
        'pesce spada',
        'pescespada',
        'anguilla',
        'aringa',
        'mackerel',
      ],
    },
    {
      name: 'Crostacei',
      keywords: [
        'gamberi',
        'gamberetti',
        'gambero',
        'scampi',
        'astice',
        'aragosta',
        'granchio',
        'canocchie',
        'canocchia',
        'cicale',
        'mazzancolle',
      ],
    },
    {
      name: 'Molluschi',
      keywords: [
        'cozze',
        'vongole',
        'ostriche',
        'calamari',
        'calamaro',
        'totani',
        'totano',
        'seppie',
        'seppia',
        'polpo',
        'polpi',
        'lumache',
        'lumaca',
        'chiocciole',
        'chiocciola',
        'seppioline',
      ],
    },
    {
      name: 'Arachidi',
      keywords: ['arachidi', 'arachide', 'burro di arachidi'],
    },
    {
      name: 'Frutta a guscio',
      keywords: [
        'noci',
        'noce',
        'mandorle',
        'mandorla',
        'nocciole',
        'nocciola',
        'pistacchi',
        'pistacchio',
        'pinoli',
        'pinolo',
        'anacardi',
        'anacardo',
        'castagne',
        'castagna',
      ],
    },
    {
      name: 'Soia',
      keywords: ['soia', 'edamame', 'tofu', 'salsa di soia', 'miso'],
    },
    {
      name: 'Sesamo',
      keywords: ['sesamo', 'tahina', 'tahini'],
    },
    {
      name: 'Sedano',
      keywords: ['sedano'],
    },
    {
      name: 'Senape',
      keywords: ['senape'],
    },
    {
      name: 'Lupini',
      keywords: ['lupini', 'lupino'],
    },
    {
      name: 'Solfiti',
      keywords: [
        'vino',
        'birra',
        'aceto',
        'uvetta',
        'albicocche secche',
        'fichi secchi',
      ],
    },
  ],
  en: [
    {
      name: 'Gluten',
      keywords: [
        'pasta',
        'spaghetti',
        'lasagna',
        'lasagne',
        'gnocchi',
        'pizza',
        'farro',
        'orzo',
        'couscous',
        'cous cous',
        'bread',
        'crostini',
        'bruschetta',
        'tortellini',
        'ravioli',
        'tagliatelle',
        'fettuccine',
        'penne',
        'linguine',
        'bucatini',
        'pappardelle',
        'maltagliati',
        'passatelli',
        'canederli',
        'calzone',
        'piadina',
        'macaroni',
      ],
    },
    {
      name: 'Dairy',
      keywords: [
        'burrata',
        'mozzarella',
        'parmesan',
        'pecorino',
        'ricotta',
        'mascarpone',
        'gorgonzola',
        'taleggio',
        'cheese',
        'stracciatella',
        'bechamel',
        'besciamella',
        'cream',
        'yogurt',
        'butter',
        'caciocavallo',
        'scamorza',
        'fontina',
        'asiago',
        'provola',
        'provolone',
        'milk',
      ],
    },
    {
      name: 'Eggs',
      keywords: [
        'eggs',
        'egg',
        'mayonnaise',
        'zabaglione',
        'tiramisu',
        'tiramisù',
        'omelette',
        'frittata',
        'carbonara',
      ],
    },
    {
      name: 'Fish',
      keywords: [
        'fish',
        'salmon',
        'tuna',
        'seabass',
        'sea bass',
        'sea bream',
        'bream',
        'cod',
        'anchovy',
        'anchovies',
        'sardine',
        'sardines',
        'trout',
        'swordfish',
        'sole',
        'mackerel',
        'hake',
        'haddock',
      ],
    },
    {
      name: 'Crustaceans',
      keywords: [
        'shrimp',
        'shrimps',
        'prawn',
        'prawns',
        'scampi',
        'lobster',
        'crab',
        'langoustine',
        'crayfish',
      ],
    },
    {
      name: 'Molluscs',
      keywords: [
        'mussels',
        'clams',
        'oysters',
        'squid',
        'cuttlefish',
        'octopus',
        'snails',
        'escargot',
      ],
    },
    {
      name: 'Peanuts',
      keywords: ['peanuts', 'peanut'],
    },
    {
      name: 'Tree nuts',
      keywords: [
        'nuts',
        'walnuts',
        'almonds',
        'hazelnuts',
        'pistachios',
        'pine nuts',
        'cashews',
        'chestnuts',
        'pecan',
        'macadamia',
      ],
    },
    {
      name: 'Soy',
      keywords: ['soy', 'soya', 'edamame', 'tofu', 'miso'],
    },
    {
      name: 'Sesame',
      keywords: ['sesame', 'tahini'],
    },
    {
      name: 'Celery',
      keywords: ['celery'],
    },
    {
      name: 'Mustard',
      keywords: ['mustard'],
    },
    {
      name: 'Lupins',
      keywords: ['lupins', 'lupini'],
    },
    {
      name: 'Sulphites',
      keywords: [
        'wine',
        'beer',
        'vinegar',
        'raisins',
        'dried apricots',
        'dried figs',
      ],
    },
  ],
}

export function inferAllergens(
  text: string,
  lang: AllergenLanguage = 'it'
): string {
  const normalized = normalize(text)
  const map = ALLERGEN_MAPS[lang] ?? ALLERGEN_MAPS.it
  const found = new Set<string>()

  for (const { name, keywords } of map) {
    if (keywords.some((keyword) => normalized.includes(normalize(keyword)))) {
      found.add(name)
    }
  }

  return Array.from(found).join(', ')
}
